import { EPaymentBrand } from "../../../setup/api/services/contract/models";
import {
  IOrganizationFeeTemplate,
  IOrganizationFeeTier,
  IOrganizationPaymentFee,
} from "../../../setup/api/services/organization/models";

// Fee preview for a two-party transaction, from the organization's active fee template.
// The seller (the organization) pays Waffy's fee, and the buyer picks the payment method only
// at checkout — so the preview charges the most expensive method the buyer will be offered,
// as the design states ("calculated at the highest rate among the buyer's payment methods").
// The amount actually charged is the backend's; this is only shown before publishing.

/** The tier the amount falls in; a tier without `to` is open-ended. */
export const findTier = (
  template: IOrganizationFeeTemplate | undefined,
  amount: number
): IOrganizationFeeTier | undefined =>
  template?.tiers?.find(
    (tier) => amount >= (tier.from ?? 0) && (!tier.to || amount <= tier.to)
  );

const feeFor = (payment: IOrganizationPaymentFee, amount: number) =>
  payment.feeType === "PERCENTAGE"
    ? amount * (payment.fees || 0)
    : payment.fees || 0;

export interface IFeeEstimate {
  fee: number;
  net: number;
}

/** Waffy's fee and the seller's net for `amount` over the given payment brands. */
export const estimateFee = (
  template: IOrganizationFeeTemplate | undefined,
  amount: number,
  brands: EPaymentBrand[]
): IFeeEstimate | undefined => {
  if (!amount || amount <= 0) return undefined;
  const tier = findTier(template, amount);
  if (!tier) return undefined;

  const fees = (tier.paymentTypes || [])
    .filter((payment) => brands.includes(payment.brand as EPaymentBrand))
    .map((payment) => feeFor(payment, amount));
  const fee = Math.round(Math.max(0, ...fees) * 100) / 100;

  return { fee, net: Math.round((amount - fee) * 100) / 100 };
};

/**
 * The fee a brand charges, for the installment labels ("Tabby — fee 5%"). Uses the tier of the
 * entered amount, or the first tier before an amount is entered.
 */
export const getBrandFeeLabel = (
  template: IOrganizationFeeTemplate | undefined,
  amount: number,
  brand: EPaymentBrand,
  currencyLabel: string
): string => {
  const tier = findTier(template, amount) || template?.tiers?.[0];
  const payment = tier?.paymentTypes?.find((type) => type.brand === brand);
  if (!payment?.fees) return "";
  if (payment.feeType === "PERCENTAGE")
    return `${+(payment.fees * 100).toFixed(2)}%`;
  return `${payment.fees} ${currencyLabel}`;
};
